import { useState, useEffect, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { ApiClientError } from '../api/client.js';
import { createProject, type Project } from '../api/projects.js';
import { listCatalogs } from '../api/catalogs.js';
import { setProjectCatalogs } from '../api/projectCatalogs.js';
import { ProjectCatalogsPicker } from './ProjectCatalogsPicker.js';
import { refreshUsageSoon } from '../stores/usageStore.js';
import { productoActivo } from '../lib/productoActivo.js';
import type { Catalog } from '@supernova/shared';

// El ejemplo del nombre habla el idioma del producto: ai cotiza licitaciones,
// ci parte de planos.
const PLACEHOLDER_NOMBRE = {
  ai: 'ej: Carretera tramo 2 — Licitación 2026',
  ci: 'ej: Casa habitación 2 niveles — Planos ejecutivos',
} as const;

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-l)' },
  // El <form> envuelve al DialogBody solo para capturar el submit (Enter). Debe
  // desaparecer del árbol de layout (`display: contents`) para que el DialogBody
  // sea hijo directo de la superficie: es la única forma en que el cuerpo (grid
  // limitado por max-height) hace scroll y el pie de acciones queda SIEMPRE
  // visible. Con el form como contenedor flex intermedio, WebKit no encoge la
  // fila de contenido y los botones se salen de pantalla al hacer zoom (125%+)
  // o en viewports chicos.
  formReset: { display: 'contents' },
});

interface Props {
  trigger: React.ReactElement;
  onCreated: (p: Project) => void;
}

export function CreateProjectDialog({ trigger, onCreated }: Props) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [availableCatalogs, setAvailableCatalogs] = useState<Catalog[]>([]);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<string[]>([]);

  // Cargar catálogos al abrir el dialog
  useEffect(() => {
    if (!open) return;
    listCatalogs()
      .then((catalogs) => {
        setAvailableCatalogs(catalogs);
        // Pre-seleccionar eCostos si está disponible
        const ecostos = catalogs.find((c) => c.kind === 'ecostos' && c.status === 'ready');
        if (ecostos && selectedCatalogIds.length === 0) {
          setSelectedCatalogIds([ecostos.id]);
        }
      })
      .catch(() => {});
  }, [open]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const p = await createProject({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      // Guardar catálogos si se seleccionó alguno
      if (selectedCatalogIds.length > 0) {
        await setProjectCatalogs(p.id, selectedCatalogIds).catch(() => {});
      }
      refreshUsageSoon();
      onCreated(p);
      setOpen(false);
      setName('');
      setDescription('');
      setSelectedCatalogIds([]);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError('Error inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <DialogTrigger disableButtonEnhancement>{trigger}</DialogTrigger>
      <DialogSurface>
        <form className={styles.formReset} onSubmit={onSubmit}>
          <DialogBody>
            <DialogTitle>Nuevo proyecto</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                {error && (
                  <MessageBar intent="error">
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}
                <Field label="Nombre" required>
                  <Input
                    value={name}
                    onChange={(_, d) => setName(d.value)}
                    autoFocus
                    disabled={submitting}
                    placeholder={PLACEHOLDER_NOMBRE[productoActivo()]}
                  />
                </Field>
                <Field label="Descripción" hint="Opcional">
                  <Textarea
                    value={description}
                    onChange={(_, d) => setDescription(d.value)}
                    disabled={submitting}
                    rows={4}
                    placeholder="Notas internas, cliente, fechas clave…"
                  />
                </Field>
                {/* Los catálogos también van en ci: se usan para BUSCAR/SUGERIR conceptos
                    a partir de los planos, no solo para el precio. */}
                {availableCatalogs.length > 0 && (
                  <ProjectCatalogsPicker
                    available={availableCatalogs}
                    initialSelected={selectedCatalogIds}
                    onChange={setSelectedCatalogIds}
                    disabled={submitting}
                  />
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" type="button" disabled={submitting}>
                  Cancelar
                </Button>
              </DialogTrigger>
              <Button appearance="primary" type="submit" disabled={submitting || !name.trim()}>
                {submitting ? 'Creando…' : 'Crear proyecto'}
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}
